import 'antd/dist/reset.css';
import 'react-toastify/dist/ReactToastify.css';

import { ConfigProvider } from 'antd';

import { AuthenticationResult, EventMessage, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import { BuildInfo, VersionInfo } from './Config';
import { SessionId } from './shared/common';
import { msalInstance } from './shared/common/auth/authConfig';
import { handleError } from './shared/common/HandleErrors';
import { isBedBoardIntakeForm, isBedBoardIntakeFormOnly, isBeta } from './shared/common/helpers';
import { bedBoardIntakeFormTheme, globalTheme } from './shared/common/theme';
import { store } from './shared/store';

let dsn = 'https://feb95ac2d7bc487bb9412eb818fad87c@o428177.ingest.sentry.io/5377331';
let release = `portal@${VersionInfo.AppVersion}`;
let appTheme = globalTheme;

if (BuildInfo.IsBedBoard) {
    dsn = 'https://0d0ada0b6a57450686983aa3e05b821e@o428177.ingest.sentry.io/4504713309847552';
    release = `bedboard@${VersionInfo.AppVersion}`;

    if (isBedBoardIntakeFormOnly()) {
        appTheme = bedBoardIntakeFormTheme;
    }
}

if (window.location?.search?.includes('version')) {
    console.log('VersionInfo:');
    console.log(VersionInfo);
}

if (window.location?.search?.includes('build')) {
    console.log('BuildInfo:');
    console.log(BuildInfo);
}

if (window.location?.search?.includes('env')) {
    console.log('EnvironmentInfo:');
    console.log(process.env);
}

Sentry.registerSpanErrorInstrumentation();

const initSentry = () => {
    Sentry.init({
        environment: window.location.hostname,
        dsn: dsn,
        release: release,
        tracesSampleRate: 1,
        // Set profilesSampleRate to 1.0 to profile every transaction.
        // Since profilesSampleRate is relative to tracesSampleRate,
        // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
        // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
        // results in 25% of transactions being profiled (0.5*0.5=0.25)
        profilesSampleRate: 1,
        ignoreErrors: ['BrowserAuthError'],
        normalizeDepth: 3,

        // Tracing headers are only attached to requests that contain localhost, medonehp.com
        // or requests whose URL starts with a '/'(for example GET /api/data)
        tracePropagationTargets: ['localhost', 'medonehp.com', /^\//],

        integrations: [
            Sentry.browserTracingIntegration({
                enableInp: true,
            }),

            Sentry.browserProfilingIntegration(),
        ],

        tracesSampler: () => {
            return isBeta() ? 1 : 0.035;
        },

        beforeBreadcrumb(breadcrumb, _hint) {
            // Remove version check requests from the breadcrumb
            return breadcrumb.category === 'fetch' && breadcrumb.message?.includes('version?code=') ? null : breadcrumb;
        },

        beforeSend(event, hint) {
            const message = (hint.originalException ?? '').toString();

            if (
                message &&
                (RegExp(/Cannot read properties of undefined (reading 'status')/i).exec(message) ||
                    RegExp(/Loading chunk/i).exec(message) ||
                    RegExp(/undefined is not an object \(evaluating 'e.status'\)/i).exec(message) ||
                    RegExp(/Loading CSS chunk/i).exec(message) ||
                    RegExp(/BrowserAuthError/i).exec(message) ||
                    RegExp(/ClientAuthError/i).exec(message) ||
                    RegExp(/Network Error/i).exec(message) ||
                    RegExp(/CanceledError/i).exec(message))
            ) {
                return null;
            }

            return event;
        },
    });

    // https://docs.sentry.io/enriching-error-data/error-tracing/#parse-the-transaction-header
    Sentry.getCurrentScope().setTag('sessionId', SessionId);
};

function importBuildTarget() {
    if (BuildInfo.IsBedBoard) {
        return isBedBoardIntakeForm() ? import('./bedboard/App-NoAuth') : import('./bedboard/App');
    }

    return import('./post-acute/App');
}

const domNode = document.getElementById('root')!;
const root = createRoot(domNode);

importBuildTarget().then(({ default: TargetApp }) => {
    initSentry();

    // For noauth area, don't apply the msal component
    if (TargetApp.displayName.includes('AppNoAuth')) {
        root.render(
            <Provider store={store}>
                <ConfigProvider theme={globalTheme} tag={{ variant: 'solid' }} getPopupContainer={() => document.getElementById('popup-containers')}>
                    <TargetApp />
                </ConfigProvider>
            </Provider>
        );
    } else {
        msalInstance
            .initialize()
            .then(async () => {
                // Default to using the first account if no account is active on page load
                if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
                    // Account selection logic is app dependent. Adjust as needed for different use cases.
                    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
                }

                // Optional - Enable account storage events to sync auth state across tabs/windows
                // This method may not be available in all MSAL versions
                if (typeof (msalInstance as any).enableAccountStorageEvents === 'function') {
                    (msalInstance as any).enableAccountStorageEvents();
                }

                // Register event callback with cleanup management
                msalInstance.addEventCallback(async (event: EventMessage) => {
                    if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
                        const authResult = event.payload as AuthenticationResult | undefined;
                        const account = authResult?.account ?? msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

                        if (account) {
                            if (!msalInstance.getActiveAccount()) {
                                console.log('MSAL auth success, setting active account');
                            }

                            msalInstance.setActiveAccount(account);

                            Sentry.setUser({ email: account.username });
                        } else {
                            console.warn('MSAL auth success event without an account payload/cache.');
                        }
                    } else if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
                        // Token acquisition failures are handled inside acquireAccessToken()
                        // with retries and single-flight deduplication. Only log here to
                        // avoid triggering additional concurrent renewal attempts.
                        console.warn('MSAL token acquisition failed:', (event.error as any)?.errorCode || event.error);
                    }
                });

                root.render(
                    <Provider store={store}>
                        <MsalProvider instance={msalInstance}>
                            <ConfigProvider theme={appTheme} tag={{ variant: 'solid' }} getPopupContainer={() => document.getElementById('popup-containers')}>
                                <TargetApp />
                            </ConfigProvider>
                        </MsalProvider>
                    </Provider>
                );
            })
            .catch((err) => {
                handleError(err);
            });
    }
});

const orgConsoleError = console.error;

console.error = (...args) => {
    const textValue = String(args[0]);

    if (textValue.includes(`Can't perform a React state update on a component that hasn't mounted yet`)) return;
    if (textValue.includes(`Support for defaultProps will be removed from function components in a future major release`)) return;
    if (textValue.includes(`findDOMNode is deprecated and will be removed in the next major release.`)) return;
    if (textValue.includes('Invalid DOM property')) return;
    if (textValue.includes('Consider using an empty string to clear the component or')) return;
    if (textValue.includes('Support for defaultProps will be removed from memo components in a future major release')) return;
    if (
        textValue.includes('InteractionRequiredAuthError') ||
        textValue.includes('interaction_required') ||
        textValue.includes('refresh_token_expired') ||
        textValue.includes('block_iframe_reload')
    ) {
        return;
    }

    orgConsoleError(...args);
};
